import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Company, CompanyListFilters } from '../models/companies.model';
import { CompaniesResponse } from '../models/companies-response.model';

@Injectable({ providedIn: 'root' })
export class CompaniesService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Fetch paginated companies
   */
  getCompanies(
    pageNumber: number = 1,
    pageSize: number = 25,
    orderBy: string = 'name',
    filters?: CompanyListFilters,
  ): Observable<CompaniesResponse | null> {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize))
      .set('orderBy', orderBy);

    const searchTerm = String(filters?.searchTerm ?? '').trim();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return this.http
      .get<CompaniesResponse>(`${this.baseUrl}/api/companies`, { params })
      .pipe(
        catchError((err) => {
          console.error('Error fetching companies:', err);
          return of(null);
        }),
      );
  }

  /**
   * Get company by ID
   */
  getCompanyById(id: number): Observable<Company | null> {
    return this.http
      .get<{ status: string; data: Company }>(`${this.baseUrl}/api/companies/${id}`)
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          console.error('Error fetching company by ID:', err);
          return of(null);
        }),
      );
  }

  /**
   * Create company
   */
  createCompany(payload: Partial<Company>): Observable<boolean> {
    return this.http
      .post<{ status: string }>(`${this.baseUrl}/api/companies`, payload)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error creating company:', err);
          return of(false);
        }),
      );
  }

  /**
   * Update company
   */
  updateCompany(id: number, payload: Partial<Company>): Observable<boolean> {
    return this.http
      .patch<{ status: string }>(`${this.baseUrl}/api/companies/${id}`, payload)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error updating company:', err);
          return of(false);
        }),
      );
  }

  /**
   * Delete company
   */
  deleteCompany(id: number): Observable<boolean> {
    return this.http
      .delete<{ status: string }>(`${this.baseUrl}/api/companies/${id}`)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error deleting company:', err);
          return of(false);
        }),
      );
  }
}